import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { get, startCase } from 'lodash-es';
import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { data as enums } from '~constants/enums';
import { FormStateChangeProps } from '~formsShared/types';
import { Company, CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { Nullable } from '~types/Nullable';

import { connectionValidationSchema } from './connectionValidationSchema';

interface AddConnectionFormProps {
  senderCompanyId: string;
  onFormStateChange: ({ isValid, isDirty }: FormStateChangeProps) => void;
}

interface ReceiverCompanyVerificationInfoTableProps {
  companyData: Company;
}

export interface AddConnectionFormValues {
  senderCompanyId: string;
  receiverTreadId: Nullable<string>;
  accountTypes: AccountType[];
  receiverCompany: Nullable<CompanyBasic>;
}

export interface AddConnectionFormHandler {
  submit: (callBack: SubmitHandler<AddConnectionFormValues>) => void;
  resetForm: (callBack?: () => void) => void;
}

const getFullAddressDisplay = (address: Company['address']) => {
  if (address) {
    const { streetAddress, premise, country, administrativeArea } = address;

    return `${streetAddress} ${premise}, ${administrativeArea}${country?.name ? `, ${country?.name}` : ''}`;
  }

  return '';
};

const DividerWithYMargin = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const TableHeading = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  padding: '8px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.semantic.surfaceSunken,
  fontSize: '12px',
  whiteSpace: 'nowrap',
}));

const TableValue = styled(TableCell)({
  whiteSpace: 'nowrap',
});

const CompanyInfoVerificationContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

// Typescript limitation where MUI component prop types
// Get lost when using styled components.
// This is a workaround to fix the issue as documented here:
// https://github.com/mui/material-ui/issues/15695#issuecomment-1026602904
const CompanyInfoVerificationHeading = styled(Typography)<{
  component?: React.ElementType;
}>(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
}));

const ReceiverCompanyVerificationInfoTable = ({
  companyData,
}: ReceiverCompanyVerificationInfoTableProps) => {
  const addressDisplay = getFullAddressDisplay(companyData.address);

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableHeading component="th">{t('common.tread_id')}</TableHeading>
            <TableHeading component="th">{t('common.name')}</TableHeading>
            <TableHeading component="th">{t('common.company_type')}</TableHeading>
            <TableHeading component="th">{t('common.usage_status')}</TableHeading>
            <TableHeading component="th">{t('common.billing_status')}</TableHeading>
            <TableHeading component="th">{t('common.plan')}</TableHeading>
            <TableHeading component="th">{t('common.address')}</TableHeading>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow
            key={companyData.id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableValue scope="row">{companyData.treadId}</TableValue>
            <TableValue>
              <Typography component="span">{companyData.legalName}</Typography>
            </TableValue>
            <TableValue>
              <Typography component="span">
                {startCase(get(companyData, 'companyType') as string)}
              </Typography>
            </TableValue>
            <TableValue>
              <Typography component="span">
                {startCase(get(companyData, 'usageStatus', '') as string)}
              </Typography>
            </TableValue>
            <TableValue>
              <Typography component="span">
                {startCase(get(companyData, 'billingStatus') as string)}
              </Typography>
            </TableValue>
            <TableValue>
              <Typography component="span">
                {startCase(get(companyData, 'saasBillingPlan') as string)}
              </Typography>
            </TableValue>
            <TableValue>
              <Typography component="span">{addressDisplay}</Typography>
            </TableValue>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AddConnectionForm = forwardRef(function AddConnectionForm(
  { senderCompanyId, onFormStateChange }: AddConnectionFormProps,
  ref: Ref<AddConnectionFormHandler>,
) {
  const { getCompanyById } = useCompany();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid, isDirty },
    watch,
  } = useForm<AddConnectionFormValues>({
    resolver: yupResolver(connectionValidationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      senderCompanyId: senderCompanyId || '',
      receiverTreadId: null,
      accountTypes: [],
      receiverCompany: null,
    },
  });
  const selectedReceiverCompany: Nullable<CompanyBasic> = watch('receiverCompany');

  const [selectedReceiverCompanyVerificationInfo, setReceiverCompanyVerificationInfo] =
    useState<Nullable<Company>>(null);
  const [isReceiverCompanyLoading, setIsReceiverCompanyLoading] = useState(false);

  // Allows for ref to be used to call form methods
  // And submit form from parent component
  useImperativeHandle(ref, () => ({
    submit(callBack: SubmitHandler<AddConnectionFormValues>) {
      handleSubmit(callBack)();
    },
    resetForm(callBack?: () => void) {
      reset();
      callBack?.();
    },
  }));

  useEffect(() => {
    onFormStateChange({ isDirty, isValid });
  }, [isValid, isDirty]);

  const getCompanyByIdRef = useRef(getCompanyById);
  getCompanyByIdRef.current = getCompanyById;

  const selectedReceiverCompanyId = selectedReceiverCompany?.id;

  // The company typeahead response carries no tread_id, so it comes from the company detail request
  useEffect(() => {
    setValue('receiverTreadId', null);
    setReceiverCompanyVerificationInfo(null);

    if (!selectedReceiverCompanyId) {
      setIsReceiverCompanyLoading(false);

      return;
    }

    const controller = new AbortController();

    const loadReceiverCompany = async () => {
      setIsReceiverCompanyLoading(true);

      try {
        const company = await getCompanyByIdRef.current({
          id: selectedReceiverCompanyId,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        setReceiverCompanyVerificationInfo(company ?? null);
        setValue('receiverTreadId', company?.treadId || null, { shouldValidate: true });
      } catch {
        // getCompanyById already reported the error
      } finally {
        if (!controller.signal.aborted) {
          setIsReceiverCompanyLoading(false);
        }
      }
    };

    void loadReceiverCompany();

    return () => controller.abort();
  }, [selectedReceiverCompanyId, setValue]);

  return (
    <>
      <Box data-test-id="company-form" sx={{ mb: 1 }}>
        <AutocompleteFormField
          control={control}
          multiple={true}
          name="accountTypes"
          errors={errors}
          list={enums.account_type.values}
          placeholder={t('account.form.account_type')}
          isRequired={true}
          getValue={(item) => item}
          getLabel={(item) => startCase(item)}
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <CompanySelectorFormField
          control={control}
          disablePortal={false}
          errors={errors}
          hideLabel={true}
          inputProps={{ placeholder: t('form_fields.company_selector_hint') }}
          isRequired={true}
          name="receiverCompany"
          sx={{ flexGrow: 1, flexShrink: 0 }}
        />
      </Box>

      {(isReceiverCompanyLoading || selectedReceiverCompanyVerificationInfo) && (
        <>
          <DividerWithYMargin />

          <CompanyInfoVerificationContainer>
            <CompanyInfoVerificationHeading component="p">
              {'Company Info (for verification purposes)'}
            </CompanyInfoVerificationHeading>

            {isReceiverCompanyLoading ? (
              <Typography component="p" variant="body2">
                {t('actions.loading')}
              </Typography>
            ) : (
              <ReceiverCompanyVerificationInfoTable
                companyData={selectedReceiverCompanyVerificationInfo as Company}
              />
            )}
          </CompanyInfoVerificationContainer>
        </>
      )}
    </>
  );
});
export { AddConnectionForm };
